/**
 * WatchTab Sortable Components - Section items with expandable content management
 * Supports adding YouTube URLs and recordings to sections inline
 */
import { useState } from 'react';
import { Video, GripVertical, Edit2, Trash2, Eye, EyeOff, ChevronDown, ChevronUp, Plus, Youtube, X, Loader2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '../../ui/button';

export const extractYoutubeId = (url) => {
  const patterns = [/youtube\.com\/watch\?v=([^&]+)/, /youtu\.be\/([^?&]+)/, /youtube\.com\/embed\/([^?&]+)/];
  for (const pattern of patterns) {
    const match = url?.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Sortable Video Item (for featured videos list)
export const SortableVideoItem = ({ video, onEdit, onDelete, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: video.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const thumbnail = video.thumbnail_url || 
    (extractYoutubeId(video.youtube_url) ? `https://i.ytimg.com/vi/${extractYoutubeId(video.youtube_url)}/mqdefault.jpg` : null);

  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-4 p-3 bg-zinc-900 rounded-lg border ${video.is_active !== false ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-white">
        <GripVertical className="w-5 h-5" />
      </div>
      <div className="w-24 h-14 flex-shrink-0 bg-zinc-800 rounded overflow-hidden">
        {thumbnail ? <img src={thumbnail} alt={video.title} className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center"><Video className="w-6 h-6 text-zinc-600" /></div>}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium truncate">{video.title}</h4>
        <div className="flex items-center gap-2 mt-1">
          {video.category && <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">{video.category}</span>}
          {video.duration && <span className="text-xs text-zinc-500">{video.duration}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onToggle(video.id, video.is_active !== false)}
          className={`p-2 rounded-lg transition-colors ${video.is_active !== false ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'}`}>
          {video.is_active !== false ? <EyeOff className="w-4 h-4 text-zinc-400" /> : <Eye className="w-4 h-4 text-green-500" />}
        </button>
        <button onClick={() => onEdit(video)} className="p-2 hover:bg-zinc-800 rounded-lg"><Edit2 className="w-4 h-4 text-zinc-400" /></button>
        <button onClick={() => onDelete(video.id)} className="p-2 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
      </div>
    </div>
  );
};

const getSectionTypeLabel = (type) => {
  switch(type) {
    case 'games': return 'Games/Broadcasts';
    case 'videos': return 'Videos';
    case 'recordings': return 'Recordings';
    case 'custom': return 'Custom Content';
    default: return type;
  }
};

// Sortable Section Item with expandable content management
export const SortableSectionItem = ({
  section, onEdit, onDelete, onToggle,
  onAddRecording, showAddRecording, availableRecordings, onSelectRecording, onCloseAddRecording, addingRecording,
  expandedSection, onToggleExpand, onRemoveVideo, onVideoDragEnd, sensors,
  onAddYoutube, onRemoveYoutube
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const isExpanded = expandedSection === section.id;
  const videoCount = (section.videos?.length || 0) + (section.youtube_items?.length || 0);
  const [ytUrl, setYtUrl] = useState('');
  const [ytTitle, setYtTitle] = useState('');
  const [addingYt, setAddingYt] = useState(false);

  const handleAddYt = async () => {
    if (!ytUrl.trim() || !onAddYoutube) return;
    setAddingYt(true);
    await onAddYoutube(section.id, ytUrl.trim(), ytTitle.trim());
    setYtUrl('');
    setYtTitle('');
    setAddingYt(false);
  };

  return (
    <div ref={setNodeRef} style={style}
      className={`bg-zinc-900 rounded-lg border ${section.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'}`}
      data-testid={`section-item-${section.id}`}
    >
      {/* Section Header */}
      <div className="flex items-center gap-4 p-4">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-white">
          <GripVertical className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium">{section.title}</h4>
          <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1 flex-wrap">
            <span className="bg-zinc-800 px-2 py-0.5 rounded">{getSectionTypeLabel(section.section_type)}</span>
            {section.sport_filter && <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded">{section.sport_filter}</span>}
            {section.channel_filter && <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Channel filtered</span>}
            {videoCount > 0 && <span>{videoCount} items</span>}
            {section.max_items && <span>Max: {section.max_items}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onToggleExpand} className="p-2 hover:bg-zinc-800 rounded-lg" title="Manage content" data-testid={`section-expand-${section.id}`}>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
          </button>
          <button onClick={() => onToggle(section.id, section.is_active)}
            className={`p-2 rounded-lg transition-colors ${section.is_active ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'}`}>
            {section.is_active ? <EyeOff className="w-4 h-4 text-zinc-400" /> : <Eye className="w-4 h-4 text-green-500" />}
          </button>
          <button onClick={() => onEdit(section)} className="p-2 hover:bg-zinc-800 rounded-lg"><Edit2 className="w-4 h-4 text-zinc-400" /></button>
          <button onClick={() => onDelete(section.id)} className="p-2 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
        </div>
      </div>

      {/* Expanded Content Management */}
      {isExpanded && (
        <div className="border-t border-white/5 px-4 pb-4 pt-3 space-y-3">
          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => onAddRecording?.()}
              className="text-xs" data-testid={`section-add-recording-${section.id}`}>
              <Plus className="w-3 h-3 mr-1" /> Add Recording
            </Button>
          </div>

          {/* Add YouTube URL inline */}
          <div className="flex items-center gap-2">
            <Youtube className="w-4 h-4 text-red-500 flex-shrink-0" />
            <input type="text" value={ytUrl} onChange={e => setYtUrl(e.target.value)}
              placeholder="YouTube URL" className="flex-1 bg-zinc-800 border border-white/10 text-white text-sm px-3 py-1.5 rounded"
              data-testid={`section-yt-url-${section.id}`}
            />
            <input type="text" value={ytTitle} onChange={e => setYtTitle(e.target.value)}
              placeholder="Title (optional)" className="w-40 bg-zinc-800 border border-white/10 text-white text-sm px-3 py-1.5 rounded"
            />
            <Button size="sm" onClick={handleAddYt} disabled={!ytUrl.trim() || addingYt}
              className="bg-red-600 hover:bg-red-500 text-xs" data-testid={`section-add-yt-${section.id}`}>
              {addingYt ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            </Button>
          </div>

          {/* Add Recording Picker */}
          {showAddRecording && (
            <div className="bg-zinc-800 rounded p-3 border border-white/10 max-h-48 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-zinc-400 text-xs font-medium">Select a recording</span>
                <button onClick={onCloseAddRecording}><X className="w-4 h-4 text-zinc-500" /></button>
              </div>
              {availableRecordings?.length === 0 ? (
                <p className="text-zinc-500 text-xs">No published recordings available</p>
              ) : (
                <div className="space-y-1">
                  {availableRecordings?.map(rec => (
                    <button key={rec.id} onClick={() => onSelectRecording?.(rec.id)} disabled={addingRecording}
                      className="w-full flex items-center gap-2 p-2 hover:bg-zinc-700 rounded text-left transition-colors">
                      {rec.thumbnail_url && <img src={rec.thumbnail_url} alt="" className="w-10 h-6 object-cover rounded" />}
                      <span className="text-white text-xs truncate flex-1">{rec.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Current Videos/YouTube Items */}
          {(section.videos?.length > 0 || section.youtube_items?.length > 0) && (
            <div className="space-y-1">
              <span className="text-zinc-500 text-xs font-medium">Content Items</span>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onVideoDragEnd}>
                <SortableContext items={(section.videos || []).map(v => v.id)} strategy={verticalListSortingStrategy}>
                  {(section.videos || []).map((video, idx) => (
                    <SortableSectionVideoItem key={video.id} video={video} index={idx} onRemove={() => onRemoveVideo?.(video.id)} />
                  ))}
                </SortableContext>
              </DndContext>
              {(section.youtube_items || []).map((item, idx) => (
                <div key={item.id} className="flex items-center gap-3 p-2 bg-zinc-800 rounded border border-white/5">
                  <Youtube className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <div className="w-12 h-8 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                    {item.thumbnail_url && <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <span className="text-zinc-300 text-sm flex-1 truncate">{item.title}</span>
                  <button onClick={() => onRemoveYoutube?.(section.id, item.id)}
                    className="p-1 hover:bg-red-500/20 rounded transition-colors" title="Remove">
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {videoCount === 0 && section.section_type !== 'games' && (
            <p className="text-zinc-600 text-xs text-center py-2">No content items yet. Add recordings or YouTube URLs above.</p>
          )}
          {section.section_type === 'games' && (
            <p className="text-zinc-500 text-xs">This section auto-populates from completed games{section.sport_filter ? ` matching "${section.sport_filter}"` : ''}{section.channel_filter ? ' on the selected channel' : ''}.</p>
          )}
        </div>
      )}
    </div>
  );
};

// Sortable Video Item within a Section
export const SortableSectionVideoItem = ({ video, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: video.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-2 bg-zinc-800 rounded border border-white/5">
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-white">
        <GripVertical className="w-4 h-4" />
      </div>
      <span className="text-zinc-500 text-xs w-5">{index + 1}.</span>
      <div className="w-12 h-8 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
        {video.thumbnail_url ? <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center"><Video className="w-4 h-4 text-zinc-600" /></div>}
      </div>
      <span className="text-zinc-300 text-sm flex-1 truncate">{video.title}</span>
      {video.type && <span className="text-zinc-600 text-[10px] uppercase">{video.type}</span>}
      <button onClick={onRemove} className="p-1 hover:bg-red-500/20 rounded transition-colors" title="Remove">
        <Trash2 className="w-3 h-3 text-red-500" />
      </button>
    </div>
  );
};
